import { useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { saveOnboardingStep, completeOnboarding, getOnboardingStatus, updateEmailPreferences, sendPhoneOtp, verifyPhoneOtp, addEmail } from '../lib/api';
import { Lock, ChevronRight, Coins, Plus, X, Loader2, Mail, MailOpen, MailX, BellOff, Phone, CheckCircle2, PartyPopper } from 'lucide-react';
import { useEffect } from 'react';
import { useMe } from '../hooks/useProfile';
import { INTERESTS_FIELD_OPTIONS, HOBBIES_FIELD_OPTIONS, EMPLOYMENT_FIELD_OPTIONS, EDUCATION_FIELD_OPTIONS, MILITARY_STATUS_FIELD_OPTIONS, ONBOARDING_REWARD, type ProfileFieldOption } from '@swipeopinion/shared';
import ConfettiBurst from '../components/ConfettiBurst';
import SwipeOpinionLogo from '../components/brand/SwipeOpinionLogo';

// ── Constants ────────────────────────────────────────────────────────────────

const HHI_BUCKETS = [
  'Under $15,000', '$15,000 \u2013 $24,999', '$25,000 \u2013 $34,999',
  '$35,000 \u2013 $49,999', '$50,000 \u2013 $74,999', '$75,000 \u2013 $99,999',
  '$100,000 \u2013 $124,999', '$125,000 \u2013 $149,999', '$150,000 \u2013 $199,999',
  '$200,000 \u2013 $249,999', '$250,000 or more', 'Prefer not to say',
];

const RACE_OPTIONS = [
  { value: 'white', label: 'White' },
  { value: 'black', label: 'Black or African American' },
  { value: 'asian', label: 'Asian' },
  { value: 'native_hawaiian', label: 'Native Hawaiian or Pacific Islander' },
  { value: 'american_indian', label: 'American Indian or Alaska Native' },
  { value: 'other', label: 'Other Race' },
];

// B-BL-3 (2026-05-11) — Step 5 (Interests/Hobbies) renders the SHARED canonical
// option sets (INTERESTS_FIELD_OPTIONS / HOBBIES_FIELD_OPTIONS) that ProfilePage
// already uses — pills display `label` but store the canonical `value` (e.g.
// "Movies & TV" → 'movies'). Bug #10 fix: the old inline arrays stored display
// labels, which the API multi_select validator rejected (invalid_value) so the
// rows were silently dropped. Sending canonical values matches every other
// working multi-select (Step 4 pets/vehicle, ProfilePage edits).
const INTERESTS_HOBBIES_MAX = 5; // mirrors ProfilePage ChipPicker cap

// B-BL-3 decision (2026-05-11) — political_leaning is intentionally
// NOT collected during onboarding. Profile-enrichment-only by design:
// low targeting frequency + sensitive topic = collecting here would
// add friction without meaningful match-rate benefit. Members who
// answer the qual via a prescreen prompt still get it written to
// member_attribute the normal way.

// ── Pill button component ────────────────────────────────────────────────────

function Pill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`px-4 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
        selected
          ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
          : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]/50'
      }`}
    >
      {label}
    </button>
  );
}

// ── Reward ladder + locks ────────────────────────────────────────────────────

// Onboarding bonus figures read from the shared source of truth so the copy
// can't drift from the API grant. COMPLETION (50) = Tier 1 (15) + Tier 2 (35),
// both banked at /onboarding/complete; Tier 3 (40) unlocks as the profile grows,
// up to TOTAL (100).
const ONBOARDING_BONUS_NOW = ONBOARDING_REWARD.COMPLETION;
const ONBOARDING_BONUS_TOTAL = ONBOARDING_REWARD.TOTAL;

/**
 * Reward-ladder meter shown atop every onboarding step. Shows the prize (25 pts
 * banked on finish, up to 100 as the profile grows) with a fill tied to step
 * progress so the bonus is visible from step 1, not just on the done screen.
 */
function RewardLadder({ pct }: { pct: number }) {
  return (
    <div className="rounded-2xl bg-gradient-to-r from-[#5B4FE8] to-[#4338CA] text-white p-4 mb-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs opacity-90 inline-flex items-center gap-1"><Coins className="w-3.5 h-3.5" /> Banked when you finish</span>
        <span className="text-sm font-bold">
          {ONBOARDING_BONUS_NOW} <span className="opacity-70 text-xs font-medium">/ {ONBOARDING_BONUS_TOTAL} pts</span>
        </span>
      </div>
      <div className="h-2 rounded-full bg-white/25 overflow-hidden">
        <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-[11px] opacity-90 mt-2">Finish your profile to bank {ONBOARDING_BONUS_NOW} pts — earn up to {ONBOARDING_BONUS_TOTAL} as your profile grows.</p>
    </div>
  );
}

/** One calm, benefit-framed note for steps that contain permanently-set fields. */
function LockRationaleNote() {
  return (
    <div className="flex items-start gap-2 rounded-xl bg-[#EEF2FF] p-3 text-xs text-[#4338CA]">
      <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5" />
      <p>Set once for accurate, fair matching — it keeps your surveys relevant and bots out.</p>
    </div>
  );
}

/** One-line "why we ask" + privacy reassurance under a sensitive field. */
function WhyWeAsk({ children }: { children: ReactNode }) {
  return <p className="text-[11px] text-[#9CA3AF] mt-1.5 leading-relaxed">{children}</p>;
}

// C1 — per-question "why this matters" copy. The survey counts are illustrative
// (static, not a live query — by design: no backend call for a hint). They frame
// each question as what UNLOCKS matched opportunities, not a hurdle.
const WHY_THIS_MATTERS: Record<string, string> = {
  gender: 'Many studies target a specific audience — matches you to 1,200+ live surveys.',
  dob: 'Age decides which studies you qualify for — nearly every survey uses it.',
  zip: 'Location unlocks region-specific studies — 900+ live surveys filter by area.',
  relationship: 'Some studies target singles, parents, or couples — 400+ live surveys use this.',
  race: 'Brands want representative panels — opens 600+ inclusive studies to you.',
  hispanic: 'Used for representative sampling on 350+ live studies.',
  education: 'Some research targets specific education levels — 500+ live surveys use this.',
  employment: 'Work status unlocks B2B and consumer studies — 700+ live surveys use this.',
  income: 'Purchasing studies target income brackets — 650+ live surveys use this.',
  household_size: 'Household studies target specific sizes — 300+ live surveys use this.',
  home: 'Homeowner-vs-renter targeting opens 250+ live studies.',
  children: 'Parenting and family studies — 450+ live surveys use this.',
};

/**
 * "?" info affordance next to a question (C1). Toggles on tap/click (mobile) and
 * shows on hover (desktop); keyboard users focus the button and press Enter/Space
 * (which fires click → toggle). We deliberately do NOT open on focus: pointer
 * clicks fire focus THEN click, so an open-on-focus + toggle-on-click would
 * cancel out and a tap would never open. The trigger is type="button" so it never
 * submits or affects the form; the tooltip is pointer-events-none + absolutely
 * positioned so it never blocks the input below or steals a tap. aria-expanded +
 * role="tooltip" for assistive tech.
 */
export function QuestionInfo({ why }: { why: string }) {
  // Two independent signals so tap and hover don't cancel: `pinned` is toggled
  // by tap/click (mobile + keyboard Enter/Space), `hovered` tracks the mouse
  // (desktop). open = pinned || hovered. A click-toggle combined with an
  // open-on-hover/focus would net-close on a tap (which synthesises mouseenter
  // before click); separating them avoids that.
  const [pinned, setPinned] = useState(false);
  const [hovered, setHovered] = useState(false);
  const open = pinned || hovered;
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        aria-label="Why we ask this"
        aria-expanded={open}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setPinned((p) => !p); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#EEF2FF] text-[#5B4FE8] text-[10px] font-bold leading-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5B4FE8]"
      >
        ?
      </button>
      {open && (
        <span role="tooltip" className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-52 z-20 rounded-lg bg-[#111827] text-white text-[11px] font-normal leading-snug px-2.5 py-1.5 shadow-lg text-left normal-case">
          {why}
        </span>
      )}
    </span>
  );
}

/**
 * Block question label + optional "?" info affordance (C1). Renders a styled
 * <div> (not <label>) on purpose — these questions drive custom Pill/select
 * groups with no htmlFor association, so a <div> avoids nesting an interactive
 * button inside a <label>.
 */
function QuestionLabel({ children, why }: { children: ReactNode; why?: string }) {
  return (
    <div className="flex items-center gap-1.5 mb-2">
      <span className="block text-sm font-medium text-[#111827]">{children}</span>
      {why && <QuestionInfo why={why} />}
    </div>
  );
}

/**
 * Onboarding progress fill. The denominator is totalSteps + 1 so the trailing
 * segment represents the finishing screens (phone / email / completion) — the
 * bar therefore never reaches 100% while a data step is showing (T6).
 */
export function onboardingProgressPct(step: number, totalSteps: number): number {
  return Math.round((step / (totalSteps + 1)) * 100);
}

/** Rough, honest "time left" estimate (~30s per remaining profile step). */
export function onboardingTimeLeft(step: number, totalSteps: number): string {
  const estSeconds = 30 * (totalSteps - step + 1);
  return estSeconds <= 90
    ? `≈ ${estSeconds} sec left`
    : `≈ ${Math.ceil(estSeconds / 60)} min left`;
}

function MultiPill({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className={`px-4 py-2.5 rounded-full text-sm font-medium border-2 transition-all ${
        selected
          ? 'bg-[#EEF2FF] text-[#5B4FE8] border-[#5B4FE8]'
          : 'bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#5B4FE8]/50'
      }`}
    >
      {label}
    </button>
  );
}

// ── Step 1: The Basics ───────────────────────────────────────────────────────

function Step1({ onSubmit, saving }: { onSubmit: (data: Record<string, unknown>) => void; saving: boolean }) {
  const [gender, setGender] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobDay, setDobDay] = useState('');
  const [dobYear, setDobYear] = useState('');
  const [zip, setZip] = useState('');
  const [relationship, setRelationship] = useState('');

  const dob = dobYear && dobMonth && dobDay ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}` : '';
  const age = dob ? Math.floor((Date.now() - new Date(dob).getTime()) / (365.25 * 86400000)) : null;
  const valid = gender && dob && zip.length >= 5 && relationship;

  return (
    <div className="space-y-6">
      <LockRationaleNote />

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.gender}>Gender</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {[{ v: 'male', l: 'Male' }, { v: 'female', l: 'Female' }, { v: 'prefer_not', l: 'Prefer not to say' }].map(({ v, l }) => (
            <Pill key={v} label={l} selected={gender === v} onClick={() => setGender(v)} />
          ))}
        </div>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.dob}>Date of birth</QuestionLabel>
        <div className="flex gap-2">
          <select value={dobMonth} onChange={(e) => setDobMonth(e.target.value)} className="flex-1 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm">
            <option value="">Month</option>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={String(i + 1)}>{new Date(2000, i).toLocaleString('en', { month: 'long' })}</option>)}
          </select>
          <select value={dobDay} onChange={(e) => setDobDay(e.target.value)} className="w-20 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm">
            <option value="">Day</option>
            {Array.from({ length: 31 }, (_, i) => <option key={i + 1} value={String(i + 1)}>{i + 1}</option>)}
          </select>
          <select value={dobYear} onChange={(e) => setDobYear(e.target.value)} className="w-24 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm">
            <option value="">Year</option>
            {Array.from({ length: 80 }, (_, i) => { const y = new Date().getFullYear() - 18 - i; return <option key={y} value={String(y)}>{y}</option>; })}
          </select>
        </div>
        {age !== null && age >= 18 && <p className="text-xs text-[#10B981] mt-1">You are {age} years old</p>}
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.zip}>ZIP code</QuestionLabel>
        <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))} placeholder="e.g. 10001" className="w-full border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm" />
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.relationship}>Relationship status</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {['single', 'engaged', 'living_with_partner', 'married', 'divorced', 'widowed'].map((v) => (
            <Pill key={v} label={v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} selected={relationship === v} onClick={() => setRelationship(v)} />
          ))}
        </div>
      </div>

      <button
        onClick={() => valid && onSubmit({ gender, date_of_birth: dob, zip_code: zip, relationship_status: relationship })}
        disabled={!valid || saving}
        className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Continue
      </button>
    </div>
  );
}

// ── Step 2: Background ───────────────────────────────────────────────────────

export function Step2({ onSubmit, saving }: { onSubmit: (data: Record<string, unknown>) => void; saving: boolean }) {
  const [race, setRace] = useState('');
  const [hispanic, setHispanic] = useState<boolean | null>(null);
  const [education, setEducation] = useState('');
  const [employment, setEmployment] = useState('');

  const valid = race && hispanic !== null && education && employment;

  return (
    <div className="space-y-6">
      <LockRationaleNote />

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.race}>Race/Ethnicity</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {RACE_OPTIONS.map(({ value, label }) => (
            <Pill key={value} label={label} selected={race === value} onClick={() => setRace(value)} />
          ))}
        </div>
        <WhyWeAsk>Buyers use this to build representative samples. Never shared with your name.</WhyWeAsk>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.hispanic}>Are you of Hispanic, Latino, or Spanish origin?</QuestionLabel>
        <div className="flex gap-2">
          <Pill label="Yes" selected={hispanic === true} onClick={() => setHispanic(true)} />
          <Pill label="No" selected={hispanic === false} onClick={() => setHispanic(false)} />
        </div>
        <WhyWeAsk>Used for representative sampling only — kept private and never tied to your identity.</WhyWeAsk>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.education}>Education</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {EDUCATION_FIELD_OPTIONS.map(({ value, label }) => <Pill key={value} label={label} selected={education === value} onClick={() => setEducation(value)} />)}
        </div>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.employment}>Employment status</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {EMPLOYMENT_FIELD_OPTIONS.map(({ value, label }) => <Pill key={value} label={label} selected={employment === value} onClick={() => setEmployment(value)} />)}
        </div>
      </div>

      <button
        onClick={() => valid && onSubmit({ race, hispanic_origin: hispanic, education, employment_status: employment })}
        disabled={!valid || saving}
        className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Continue
      </button>
    </div>
  );
}

// ── Step 3: Household ────────────────────────────────────────────────────────

interface ChildEntry { age_years?: number; age_months?: number; gender: string }

function Step3({ onSubmit, saving }: { onSubmit: (data: Record<string, unknown>) => void; saving: boolean }) {
  const [income, setIncome] = useState('');
  const [hhSize, setHhSize] = useState('');
  const [homeOwn, setHomeOwn] = useState('');
  const [hasChildren, setHasChildren] = useState<boolean | null>(null);
  const [children, setChildren] = useState<ChildEntry[]>([]);

  const valid = income && hhSize && homeOwn && hasChildren !== null;

  function addChild() {
    if (children.length >= 6) return;
    setChildren([...children, { age_years: undefined, age_months: undefined, gender: 'boy' }]);
  }

  function updateChild(idx: number, field: string, value: string | number) {
    setChildren(children.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  }

  function removeChild(idx: number) {
    setChildren(children.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-6">
      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.income}>Household income</QuestionLabel>
        <select value={income} onChange={(e) => setIncome(e.target.value)} className="w-full border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm">
          <option value="">Select income range</option>
          {HHI_BUCKETS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <WhyWeAsk>Helps match studies to your bracket. Kept private — never shared with your name.</WhyWeAsk>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.household_size}>People in household</QuestionLabel>
        <div className="flex flex-wrap gap-2">
          {['1', '2', '3', '4', '5', '6+'].map((v) => (
            <Pill key={v} label={v} selected={hhSize === v} onClick={() => setHhSize(v)} />
          ))}
        </div>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.home}>Home ownership</QuestionLabel>
        <div className="flex gap-2">
          {['own', 'rent', 'other'].map((v) => (
            <Pill key={v} label={v.charAt(0).toUpperCase() + v.slice(1)} selected={homeOwn === v} onClick={() => setHomeOwn(v)} />
          ))}
        </div>
      </div>

      <div>
        <QuestionLabel why={WHY_THIS_MATTERS.children}>Children under 18 at home?</QuestionLabel>
        <div className="flex gap-2">
          <Pill label="Yes" selected={hasChildren === true} onClick={() => setHasChildren(true)} />
          <Pill label="No" selected={hasChildren === false} onClick={() => setHasChildren(false)} />
        </div>
      </div>

      {hasChildren && (
        <div className="space-y-3">
          {children.map((child, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-[#F8F9FA] rounded-xl p-3">
              <select
                value={child.age_years ?? ''}
                onChange={(e) => updateChild(idx, 'age_years', parseInt(e.target.value))}
                className="flex-1 border border-[#E5E7EB] rounded-lg px-2 py-2 text-sm"
              >
                <option value="">Age</option>
                {Array.from({ length: 18 }, (_, i) => <option key={i} value={i}>{i} years</option>)}
              </select>
              <select
                value={child.gender}
                onChange={(e) => updateChild(idx, 'gender', e.target.value)}
                className="w-24 border border-[#E5E7EB] rounded-lg px-2 py-2 text-sm"
              >
                <option value="boy">Boy</option>
                <option value="girl">Girl</option>
              </select>
              <button onClick={() => removeChild(idx)} className="text-[#EF4444] p-1"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {children.length < 6 && (
            <button onClick={addChild} className="text-sm text-[#5B4FE8] font-medium flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add a child
            </button>
          )}
        </div>
      )}

      <button
        onClick={() => valid && onSubmit({ household_income: income, household_size: hhSize, home_ownership: homeOwn, has_children: hasChildren, children })}
        disabled={!valid || saving}
        className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Continue
      </button>
    </div>
  );
}

// ── Step 4: Lifestyle (skippable) ────────────────────────────────────────────

export function Step4({ onSubmit, onSkip, saving }: { onSubmit: (data: Record<string, unknown>) => void; onSkip: () => void; saving: boolean }) {
  const [pets, setPets] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<string[]>([]);
  const [smoking, setSmoking] = useState('');
  const [military, setMilitary] = useState('');

  function toggleMulti(arr: string[], setArr: (v: string[]) => void, val: string) {
    if (val === 'none' || val === 'none') {
      setArr(arr.includes(val) ? [] : [val]);
    } else {
      const filtered = arr.filter((v) => v !== 'none');
      setArr(filtered.includes(val) ? filtered.filter((v) => v !== val) : [...filtered, val]);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">Pets</label>
        <div className="flex flex-wrap gap-2">
          {['dog', 'cat', 'fish', 'small_birds', 'rabbit', 'snake_lizard', 'horse', 'exotic', 'none'].map((v) => (
            <MultiPill key={v} label={v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} selected={pets.includes(v)} onClick={() => toggleMulti(pets, setPets, v)} />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">Vehicle</label>
        <div className="flex flex-wrap gap-2">
          {['sedan', 'suv', 'truck', 'minivan', 'sports', 'electric', 'hybrid', 'none'].map((v) => (
            <MultiPill key={v} label={v === 'none' ? "I don't own a vehicle" : v.charAt(0).toUpperCase() + v.slice(1)} selected={vehicles.includes(v)} onClick={() => toggleMulti(vehicles, setVehicles, v)} />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">Smoking</label>
        <div className="flex flex-wrap gap-2">
          {[{ v: 'current', l: 'Current smoker' }, { v: 'former', l: 'Former smoker' }, { v: 'never', l: 'Never smoked' }].map(({ v, l }) => (
            <Pill key={v} label={l} selected={smoking === v} onClick={() => setSmoking(v)} />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">Military status</label>
        <div className="flex flex-wrap gap-2">
          {MILITARY_STATUS_FIELD_OPTIONS.map(({ value, label }) => (
            <Pill key={value} label={value === 'civilian' ? 'Civilian (not military)' : label} selected={military === value} onClick={() => setMilitary(value)} />
          ))}
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={onSkip} className="flex-1 py-3 border-2 border-[#E5E7EB] text-[#6B7280] font-medium rounded-full hover:bg-gray-50">
          Skip for now
        </button>
        <button
          onClick={() => onSubmit({ pets, vehicle_types: vehicles, smoking_status: smoking || undefined, military_status: military || undefined })}
          disabled={saving}
          className="flex-1 py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          Continue
        </button>
      </div>
    </div>
  );
}

// ── Step 5: Interests (optional) ─────────────────────────────────────────────
//
// B-BL-3 (2026-05-11). Two chip-pickers, capped at 5 each, mirroring the
// ProfilePage ChipPicker pattern. Skipping writes nothing — the API
// handler only persists non-empty arrays.

function ChipGroup({
  label, options, selected, onChange,
}: {
  label: string;
  // Canonical {value,label} option set — pills display `label`, but `selected`
  // and the submitted payload hold the canonical `value` (bug #10 fix).
  options: ProfileFieldOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  function toggle(value: string) {
    if (selected.includes(value)) {
      onChange(selected.filter((s) => s !== value));
    } else if (selected.length < INTERESTS_HOBBIES_MAX) {
      onChange([...selected, value]);
    }
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-[#111827]">{label}</label>
        <span className="text-xs text-[#9CA3AF]">{selected.length}/{INTERESTS_HOBBIES_MAX}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <MultiPill key={opt.value} label={opt.label} selected={selected.includes(opt.value)} onClick={() => toggle(opt.value)} />
        ))}
      </div>
    </div>
  );
}

export function Step5({ onSubmit, onSkip, saving }: { onSubmit: (data: Record<string, unknown>) => void; onSkip: () => void; saving: boolean }) {
  const [interests, setInterests] = useState<string[]>([]);
  const [hobbies, setHobbies] = useState<string[]>([]);

  return (
    <div className="space-y-6">
      <div className="bg-[#EEF2FF] border border-[#5B4FE8]/20 rounded-xl p-3 text-xs text-[#4338CA]">
        <span className="font-medium">Optional.</span> Tell us what you're into and we'll
        find better-matching surveys for you. Skip if you'd rather not.
      </div>

      <ChipGroup
        label="Interests"
        options={INTERESTS_FIELD_OPTIONS}
        selected={interests}
        onChange={setInterests}
      />

      <ChipGroup
        label="Hobbies"
        options={HOBBIES_FIELD_OPTIONS}
        selected={hobbies}
        onChange={setHobbies}
      />

      <div className="flex gap-3">
        <button onClick={onSkip} className="flex-1 py-3 border-2 border-[#E5E7EB] text-[#6B7280] font-medium rounded-full hover:bg-gray-50">
          Skip for now
        </button>
        <button
          onClick={() => onSubmit({ interests, hobbies })}
          disabled={saving}
          className="flex-1 py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          Finish · +{ONBOARDING_BONUS_NOW} pts
        </button>
      </div>
    </div>
  );
}

// ── Phone Verification Step ─────────────────────────────────────────────────

function formatPhoneDisplay(digits: string): string {
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function StepPhone({ onDone, alreadyVerified }: { onDone: () => void; alreadyVerified: boolean }) {
  const [digits, setDigits] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [verified, setVerified] = useState(alreadyVerified);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  const phoneNumber = `+1${digits}`;
  const canSend = digits.length === 10 && smsOptIn;

  // Auto-skip if already verified
  useEffect(() => {
    if (alreadyVerified) {
      const t = setTimeout(onDone, 1000);
      return () => clearTimeout(t);
    }
  }, [alreadyVerified, onDone]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  async function handleSendCode() {
    setSending(true);
    setError(null);
    try {
      await sendPhoneOtp(phoneNumber);
      setCodeSent(true);
      setResendTimer(30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    }
    setSending(false);
  }

  async function handleVerify() {
    setSending(true);
    setError(null);
    try {
      await verifyPhoneOtp(phoneNumber, otp, smsOptIn);
      setVerified(true);
      setTimeout(onDone, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    }
    setSending(false);
  }

  if (verified) {
    return (
      <div className="flex flex-col items-center text-center space-y-4 py-6">
        <CheckCircle2 className="w-16 h-16 text-[#10B981]" />
        <h2 className="text-xl font-bold text-[#111827]">{alreadyVerified ? 'Phone already verified' : 'Phone verified!'}</h2>
        <p className="text-sm text-[#6B7280]">{alreadyVerified ? 'Moving on...' : "You'll receive SMS alerts for high-value surveys."}</p>
      </div>
    );
  }

  if (codeSent) {
    return (
      <div className="space-y-6 py-2">
        <div className="text-center">
          <Phone className="w-10 h-10 text-[#5B4FE8] mx-auto mb-2" />
          <h2 className="text-xl font-bold text-[#111827]">Enter verification code</h2>
          <p className="text-sm text-[#6B7280] mt-1">Sent to {formatPhoneDisplay(digits)}</p>
        </div>

        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          className="w-full text-center text-2xl tracking-[0.5em] font-mono border border-[#E5E7EB] rounded-xl px-4 py-4 focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
        />

        {error && <p className="text-[#EF4444] text-sm text-center">{error}</p>}

        <button
          onClick={() => void handleVerify()}
          disabled={otp.length < 6 || sending}
          className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          Verify
        </button>

        <div className="flex justify-center gap-4 text-sm">
          {resendTimer > 0 ? (
            <span className="text-[#9CA3AF]">Resend in {resendTimer}s</span>
          ) : (
            <button onClick={() => void handleSendCode()} className="text-[#5B4FE8] hover:underline">Resend code</button>
          )}
          <button onClick={() => { setCodeSent(false); setOtp(''); setError(null); }} className="text-[#6B7280] hover:underline">Use different number</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      <div className="text-center">
        <Phone className="w-10 h-10 text-[#5B4FE8] mx-auto mb-2" />
        <h2 className="text-xl font-bold text-[#111827]">Add your phone number</h2>
        <p className="text-sm text-[#6B7280] mt-1">Get instant alerts when high-value surveys are available. Optional — you can skip this.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-[#111827] mb-2">Phone number</label>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 px-3 py-2.5 bg-gray-50 border border-[#E5E7EB] rounded-xl text-sm text-[#6B7280]">
            <span className="text-base">🇺🇸</span> +1
          </span>
          <input
            type="tel"
            inputMode="numeric"
            value={digits.length > 0 ? formatPhoneDisplay(digits) : ''}
            onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="(555) 555-1234"
            className="flex-1 border border-[#E5E7EB] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
          />
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={smsOptIn}
          onChange={(e) => setSmsOptIn(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-[#D1D5DB] text-[#5B4FE8] focus:ring-[#5B4FE8]"
        />
        <span className="text-xs text-[#6B7280] leading-relaxed">
          By checking this box, I agree to receive recurring automated marketing
          text messages from Swipe Opinion at the number provided. Consent is not
          a condition of purchase. Msg frequency varies. Msg &amp; data rates may apply.
          Reply STOP to cancel, HELP for help.
        </span>
      </label>

      {error && <p className="text-[#EF4444] text-sm">{error}</p>}

      <button
        onClick={() => void handleSendCode()}
        disabled={!canSend || sending}
        className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Send verification code
      </button>

      <button
        onClick={onDone}
        className="w-full text-sm text-[#6B7280] hover:text-[#111827] py-2"
      >
        Skip for now
      </button>
    </div>
  );
}

// ── Email Preferences Step ──────────────────────────────────────────────────

const EMAIL_FREQ_OPTIONS = [
  { value: 'daily', label: 'Daily', desc: 'Get notified every day', Icon: MailOpen },
  { value: 'few_times_week', label: 'A few times a week', desc: 'Recommended \u00b7 2\u20133 times per week', Icon: Mail },
  { value: 'weekly', label: 'Weekly', desc: 'Once a week digest', Icon: MailX },
  { value: 'never', label: 'Never', desc: "I'll check the app myself", Icon: BellOff },
] as const;

function StepEmailPreferences({ onDone, hasEmail }: { onDone: () => void; hasEmail: boolean }) {
  const [selected, setSelected] = useState('few_times_week');
  const [saving, setSaving] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailAdded, setEmailAdded] = useState(hasEmail);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  async function handleAddEmail() {
    if (!emailInput.includes('@')) return;
    setEmailSending(true);
    setEmailError(null);
    try {
      await addEmail(emailInput);
      setEmailAdded(true);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to add email');
    }
    setEmailSending(false);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await updateEmailPreferences(selected);
    } catch { /* non-critical */ }
    setSaving(false);
    onDone();
  }

  if (!emailAdded) {
    return (
      <div className="space-y-6 py-2">
        <div className="text-center">
          <Mail className="w-10 h-10 text-[#5B4FE8] mx-auto mb-2" />
          <h2 className="text-xl font-bold text-[#111827]">Add your email</h2>
          <p className="text-sm text-[#6B7280] mt-1">We'll send survey alerts and important account updates here.</p>
        </div>

        <div>
          <input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="you@example.com"
            className="w-full border border-[#E5E7EB] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#5B4FE8] focus:ring-2 focus:ring-purple-100"
          />
        </div>

        {emailError && <p className="text-[#EF4444] text-sm">{emailError}</p>}

        <button
          onClick={() => void handleAddEmail()}
          disabled={!emailInput.includes('@') || emailSending}
          className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
        >
          {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
          Continue
        </button>

        <button onClick={onDone} className="w-full text-sm text-[#6B7280] hover:text-[#111827] py-2">
          Skip
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      <div className="text-center">
        <h2 className="text-xl font-bold text-[#111827]">How often would you like survey alerts?</h2>
        <p className="text-sm text-[#6B7280] mt-1">We'll email you when new surveys match your profile.</p>
      </div>

      <div className="space-y-3">
        {EMAIL_FREQ_OPTIONS.map(({ value, label, desc, Icon }) => (
          <button
            key={value}
            onClick={() => setSelected(value)}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
              selected === value
                ? 'border-[#5B4FE8] bg-[#EEF2FF]'
                : 'border-[#E5E7EB] hover:border-[#5B4FE8]/50'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              selected === value ? 'bg-[#5B4FE8] text-white' : 'bg-gray-100 text-[#6B7280]'
            }`}>
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <p className={`text-sm font-medium ${selected === value ? 'text-[#5B4FE8]' : 'text-[#111827]'}`}>{label}</p>
              <p className="text-xs text-[#6B7280]">{desc}</p>
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={() => void handleSave()}
        disabled={saving}
        className="w-full py-3 bg-[#5B4FE8] text-white font-medium rounded-full disabled:opacity-40 hover:bg-[#4338CA] transition-colors flex items-center justify-center gap-2"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
        Continue
      </button>

      <button
        onClick={onDone}
        className="w-full text-sm text-[#6B7280] hover:text-[#111827] py-2"
      >
        Skip
      </button>
    </div>
  );
}

// ── Completion Screen ────────────────────────────────────────────────────────

function StepCompletion({ pointsAwarded, onDone }: { pointsAwarded: number; onDone: () => void }) {
  return (
    <div className="flex flex-col items-center text-center space-y-6 py-4">
      <ConfettiBurst />
      <PartyPopper className="w-16 h-16 text-[#5B4FE8]" />
      <div>
        <h2 className="text-2xl font-bold text-[#111827]">You're all set!</h2>
        <p className="text-[#6B7280] mt-1">Your profile is now ready for surveys.</p>
      </div>

      {pointsAwarded > 0 && (
        <div className="inline-flex items-center gap-2 bg-[#10B981]/10 text-[#10B981] rounded-full px-5 py-3 font-bold">
          <Coins className="w-5 h-5" />
          +{pointsAwarded} points earned!
        </div>
      )}

      <p className="text-xs text-[#9CA3AF] max-w-sm">
        Your demographic profile is now locked for accuracy. You can still update your address, income, employment and lifestyle at any time.
      </p>

      <button
        onClick={onDone}
        className="w-full max-w-sm py-3 bg-[#5B4FE8] hover:bg-[#4338CA] text-white font-medium rounded-full transition-colors"
      >
        Start Earning
      </button>
    </div>
  );
}

// ── Main OnboardingPage ──────────────────────────────────────────────────────

const STEP_LABELS = ['The Basics', 'Background', 'Household', 'Lifestyle', 'Interests'];
const TOTAL_STEPS = STEP_LABELS.length;

/**
 * A2 — onboarding intro. The login page's calmer sibling: same gradient + lens-
 * flare background, same type hierarchy and white pill CTA, so login → intro →
 * form reads as one entrance. Frames the questions as what UNLOCKS earning and
 * states the reward using the SAME constants as the RewardLadder banner (25 pts
 * on finish, up to 100 as the profile grows). "Get started" advances into Step 1.
 */
export function OnboardingIntro({ onStart }: { onStart: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#5B4FE8] via-[#4338CA] to-teal-500 relative overflow-hidden px-4">
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full bg-white/10" />
      <div className="absolute bottom-32 right-16 w-48 h-48 rounded-full bg-white/5" />
      <div className="absolute top-1/3 right-1/4 w-20 h-20 rounded-2xl bg-white/10 rotate-12" />

      <div className="max-w-md w-full relative z-10 text-white text-center">
        <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mx-auto mb-4">
          <SwipeOpinionLogo variant="mark" markVariant="white" markClassName="w-7 h-7" />
        </div>
        <h1 className="text-3xl font-bold leading-tight">You're in. Let's get you matched.</h1>
        <p className="text-purple-100 text-base mt-3 leading-relaxed">
          A few quick questions so we can match you to the best-paying, most relevant surveys — and skip the ones you'd never qualify for.
        </p>

        <div className="mt-6 inline-flex items-center gap-2 bg-white/15 rounded-full px-5 py-2.5 text-sm font-semibold">
          <Coins className="w-5 h-5" /> Bank {ONBOARDING_BONUS_NOW} pts when you finish · up to {ONBOARDING_BONUS_TOTAL} as your profile grows
        </div>

        <button
          onClick={onStart}
          className="w-full mt-8 py-3.5 px-6 bg-white text-[#5B4FE8] font-bold rounded-full hover:bg-gray-50 transition flex items-center justify-center gap-2 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#4338CA]"
        >
          Get started <ChevronRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-purple-200 mt-4">Takes ~2 minutes</p>
      </div>
    </div>
  );
}

export default function OnboardingPage() {
  const [step, setStep] = useState(1);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showPhoneStep, setShowPhoneStep] = useState(false);
  const [showEmailPrefs, setShowEmailPrefs] = useState(false);
  const [complete, setComplete] = useState(false);
  // A2 — intro screen shown once for NEW users before Step 1 (the calm sibling
  // of the login page). Resuming users (onboarding_step > 0) skip it; it's never
  // re-shown after completion (that path navigates to /dashboard).
  const [showIntro, setShowIntro] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();

  // Resume from last step
  useEffect(() => {
    getOnboardingStatus()
      .then((status) => {
        if (status.onboarding_complete) {
          navigate('/dashboard', { replace: true });
        } else if (status.onboarding_step > 0) {
          setStep(Math.min(status.onboarding_step + 1, TOTAL_STEPS));
          setShowIntro(false); // mid-onboarding return → straight to the form
        }
      })
      .catch(() => {});
  }, [navigate]);

  async function handleStepSubmit(stepNum: number, data: Record<string, unknown>) {
    setSaving(true);
    try {
      await saveOnboardingStep(stepNum, data);
      if (stepNum < TOTAL_STEPS) {
        setStep(stepNum + 1);
      } else {
        await finishOnboarding();
      }
    } catch (err) {
      console.error('[onboarding] Step save failed:', err);
    }
    setSaving(false);
  }

  // B-BL-3 — skipping Step 4 (Lifestyle) advances to Step 5 (Interests),
  // not straight to completion. Each optional step has its own skip path.
  function handleSkipStep4() {
    setStep(5);
  }

  async function handleSkipStep5() {
    // Step 5 is the last data step; skipping it finalises onboarding
    // without writing any hobbies/interests attributes.
    await finishOnboarding();
  }

  async function finishOnboarding() {
    setSaving(true);
    try {
      const result = await completeOnboarding();
      setPointsAwarded(result.points_awarded);
      setShowPhoneStep(true);
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      // GA4 onboarding_complete fires from handleDone() — there it's gated by
      // event_callback so navigation waits for the hit to flush.
    } catch (err) {
      console.error('[onboarding] Complete failed:', err);
    }
    setSaving(false);
  }

  function handlePhoneDone() {
    setShowPhoneStep(false);
    setShowEmailPrefs(true);
  }

  function handleEmailPrefsDone() {
    setShowEmailPrefs(false);
    setComplete(true);
  }

  function handleDone() {
    void queryClient.invalidateQueries({ queryKey: ['me'] });
    navigate('/dashboard');
  }

  // A2 — new users see the intro first (advances forward into Step 1; never
  // bypasses onboarding). Resuming/finishing users never reach this.
  if (showIntro && !complete && !showPhoneStep && !showEmailPrefs) {
    return <OnboardingIntro onStart={() => setShowIntro(false)} />;
  }

  // Honest progress: never reads 100% while finishing screens still follow.
  const progressPct = onboardingProgressPct(step, TOTAL_STEPS);
  const timeLeft = onboardingTimeLeft(step, TOTAL_STEPS);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-lg">
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center mb-1">
            <SwipeOpinionLogo variant="full" markClassName="w-6 h-6" wordmarkClassName="text-2xl text-navy" />
          </div>
        </div>

        {!complete && !showPhoneStep && !showEmailPrefs && (
          <div className="mb-6">
            <RewardLadder pct={progressPct} />
            <div className="flex justify-between text-xs mb-1">
              <span className="text-[#6B7280]">{STEP_LABELS[step - 1]} · {step} of {TOTAL_STEPS}</span>
              <span className="text-[#10B981] font-medium">{timeLeft}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-[#5B4FE8] h-2 rounded-full transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm p-6">
          {!complete && !showPhoneStep && !showEmailPrefs && step === 1 && <Step1 onSubmit={(d) => void handleStepSubmit(1, d)} saving={saving} />}
          {!complete && !showPhoneStep && !showEmailPrefs && step === 2 && <Step2 onSubmit={(d) => void handleStepSubmit(2, d)} saving={saving} />}
          {!complete && !showPhoneStep && !showEmailPrefs && step === 3 && <Step3 onSubmit={(d) => void handleStepSubmit(3, d)} saving={saving} />}
          {!complete && !showPhoneStep && !showEmailPrefs && step === 4 && <Step4 onSubmit={(d) => void handleStepSubmit(4, d)} onSkip={handleSkipStep4} saving={saving} />}
          {!complete && !showPhoneStep && !showEmailPrefs && step === 5 && <Step5 onSubmit={(d) => void handleStepSubmit(5, d)} onSkip={() => void handleSkipStep5()} saving={saving} />}
          {showPhoneStep && <StepPhone onDone={handlePhoneDone} alreadyVerified={me?.phone_verified ?? false} />}
          {showEmailPrefs && <StepEmailPreferences onDone={handleEmailPrefsDone} hasEmail={!!me?.member?.email} />}
          {complete && <StepCompletion pointsAwarded={pointsAwarded} onDone={handleDone} />}

          {!complete && step > 1 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <button onClick={() => setStep((s) => s - 1)} className="text-sm text-[#6B7280] hover:text-[#111827]">
                Back
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
